import React, { useCallback, useEffect, useMemo, useState } from "react"
import { CircularProgress } from "@mui/material"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery } from "@apollo/client/react"
import i18n from "@/i18n"
import {
  GetVenueFullDocument,
  GetTagDefinitionsDocument,
  type GetVenueFullQuery,
  type GetVenueFullQueryVariables,
  type GetTagDefinitionsQuery,
} from "@/graphql/graphql"
import VenueInformation, { type CategoryState, buildStateFromTags } from "@/components/settings/venue_information"
import VenueTags from "@/components/venue_tags"
import type { VenueTagDisplay } from "@/components/venue_tags/types"
import { CATEGORY_DISPLAY_ORDER } from "@/constants/venue_tags"
import { usePanelLink } from "@/shared/hooks"

const buildPreviewFromState = (
  state: CategoryState,
  definitions: GetTagDefinitionsQuery["tagDefinitions"],
): VenueTagDisplay[] => {
  const defMap = new Map(definitions.map((d) => [d.key, d]))

  return CATEGORY_DISPLAY_ORDER.reduce<VenueTagDisplay[]>((acc, key) => {
    const values = state[key]
    if (values?.length) {
      const def = defMap.get(key)
      const optionMap = new Map((def?.options || []).map((o) => [o.value, o.label]))
      const labels = values.map((v) => optionMap.get(v) || v)
      acc.push({
        category: key,
        label: def?.label || i18n.t(`app.venue_tags.categories.${key}`),
        values: labels.join(", "),
      })
    }
    return acc
  }, [])
}

const VenueInformationPage: React.FC = () => {
  const { venueSlug } = useParams()
  const navigate = useNavigate()
  const linkTo = usePanelLink()

  const { data, loading, error, refetch } = useQuery<GetVenueFullQuery, GetVenueFullQueryVariables>(
    GetVenueFullDocument,
    {
      variables: { slug: venueSlug },
    },
  )

  const { data: defData } = useQuery<GetTagDefinitionsQuery>(GetTagDefinitionsDocument)

  const venue = data?.venue
  const definitions = defData?.tagDefinitions || []

  useEffect(() => {
    if (venue && !venue.permissions?.manageVenue) {
      navigate(linkTo("settings"))
    }
  }, [venue?.permissions?.manageVenue, navigate, linkTo])

  const [editorState, setEditorState] = useState<CategoryState>(() => buildStateFromTags(venue?.tags || []))

  const handleStateChange = useCallback((state: CategoryState) => {
    setEditorState(state)
  }, [])

  const previewCategories = useMemo(() => buildPreviewFromState(editorState, definitions), [editorState, definitions])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return <div className="text-red-500 p-8">Error loading venue: {error.message}</div>
  }

  if (!venue) {
    return <div className="p-8">Venue not found</div>
  }

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <h1 className="text-3xl font-bold mb-0" data-testid="settings-venue-information-title">
        {i18n.t("app.settings.venues.venue_information.title")}
      </h1>
      <p className="text-sm text-gray-500 mt-0">{i18n.t("app.settings.venues.venue_information.subtitle")}</p>
      <VenueTags
        header={i18n.t("app.settings.venues.venue_information.preview")}
        categories={previewCategories}
        testId="settings-venue-information-preview"
      />
      <VenueInformation
        venueId={venue.id}
        tags={venue.tags}
        definitions={definitions}
        onRefetch={refetch}
        onStateChange={handleStateChange}
      />
    </div>
  )
}

export default VenueInformationPage
